import React from "react";
import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "profile";
  twitterCard?: "summary" | "summary_large_image";
  children?: React.ReactNode;
}

export const SEO: React.FC<SEOProps> = ({
  title = "Axentra Health",
  description = "Axentra Health - Innovative healthcare solutions transforming patient care and clinical workflows. Elevating healthcare delivery through technology and personalized services.",
  canonicalUrl = "/",
  keywords = "healthcare, patient care, clinical workflows, healthcare technology, digital health",
  ogImage = "/og-image.jpg",
  ogType = "website",
  twitterCard = "summary_large_image",
  children,
}) => {
  const siteUrl = "https://www.axentrahealth.com";
  const fullTitle =
    title === "Axentra Health" ? title : `${title} | Axentra Health`;

  const resolveUrl = (url: string) => {
    if (!url.startsWith("http")) return `${siteUrl}${url}`;
    // Normalize non-www to www
    return url.replace(/^https?:\/\/(?!www\.)axentrahealth\.com/, siteUrl);
  };

  const canonicalHref = resolveUrl(canonicalUrl);
  const ogUrl = canonicalHref;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalHref} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={ogUrl} />
      <meta
        property="og:image"
        content={ogImage.startsWith("http") ? ogImage : `${siteUrl}${ogImage}`}
      />
      <meta property="og:site_name" content="Axentra Health" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta
        name="twitter:image"
        content={ogImage.startsWith("http") ? ogImage : `${siteUrl}${ogImage}`}
      />

      {/* Additional Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />

      {children}
    </Helmet>
  );
};
